import React, { useRef, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import keyBy from "lodash/keyBy";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { SHOPPING_INVENTORY_CARTS_QUERY } from "../../../../constants/graphql";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  DELETE_INVENTORY_ITEMS_MUTATION,
  UPDATE_SHOPPING_INVENTORY_ITEM_MUTATION,
} from "../../../../constants/graphql/mutations";
import useMutation from "src/hooks/useMutation";
import { usePermissions } from "../../../../contexts/permissions";
import { getImage } from "../../../../views/shopping/Cart/CartItemCard";
import { ProjectProvider } from "../../../../contexts/project";
import ItemIdAssetModal from "../../../../views/shopping/Orders/ItemIdAssetModal";
import ConfirmDeleteModal from "../../../../components/images/ConfirmDeleteModal";
import QuantityEditModal from "../../../../views/inventory/requests/QuantityEditModal";

const styles = {
  badge: {
    "& .MuiBadge-badge": {
      fontSize: "0.65rem",
      minWidth: "16px",
      height: "16px",
      fontWeight: 700,
    },
  },
  popover: {
    "& .MuiPopover-paper": {
      width: 360,
      borderRadius: "12px",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: "0 12px 40px -8px rgba(0,0,0,0.15), 0 4px 12px -2px rgba(0,0,0,0.08)",
      overflow: "hidden",
    },
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    py: 2,
    px: 2.5,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
  thumbnail: {
    width: 72,
    height: 72,
    borderRadius: "8px",
    border: "1px solid",
    borderColor: "grey.200",
    backgroundColor: "#f8f9fb",
    objectFit: "contain",
    flexShrink: 0,
    p: "4px",
  },
  thumbnailPlaceholder: {
    width: 72,
    height: 72,
    borderRadius: "8px",
    border: "1px solid",
    borderColor: "grey.200",
    backgroundColor: "#f8f9fb",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "grey.400",
  },
  scrollArea: {
    maxHeight: 380,
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "grey.300",
      borderRadius: 3,
    },
  },
};

export default function ShoppingCart() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [quantityItem, setQuantityItem] = useState(null);
  const [clearCartOpen, setClearCartOpen] = useState(false);
  const [removeItem, setRemoveItem] = useState(null);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [deleteInventoryItems] = useMutation(DELETE_INVENTORY_ITEMS_MUTATION);
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [updateInventoryItem] = useMutation(UPDATE_SHOPPING_INVENTORY_ITEM_MUTATION);

  const hasShopping = can("pages.shopping") || can("pages.shopping_cart");

  const { data, refetch } = useQuery(SHOPPING_INVENTORY_CARTS_QUERY, {
    skip: !hasShopping,
    fetchPolicy: "cache-and-network",
  });

  const inventoryRequests = data?.shoppingInventoryCarts || [];
  const inventoryItems = inventoryRequests.flatMap(cart => cart.inventoryItems || []);
  const assets = useMemo(
    () => keyBy(inventoryRequests.flatMap(cart => cart.assets || []), "id"),
    [inventoryRequests]
  );
  const itemCount = inventoryItems.length;

  useEffect(() => {
    const openHandler = () => {
      refetch();
      setOpen(true);
    };
    const refreshHandler = () => refetch();

    window.addEventListener("openShoppingCart", openHandler);
    window.addEventListener("refreshShoppingCart", refreshHandler);
    return () => {
      window.removeEventListener("openShoppingCart", openHandler);
      window.removeEventListener("refreshShoppingCart", refreshHandler);
    };
  }, [refetch]);

  if (!hasShopping) return null;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCheckout = () => {
    handleClose();
    navigate("/shopping/cart?checkout=true");
  };

  const handleClearCart = () => {
    const ids = inventoryItems.map(item => item.id);
    return deleteInventoryItems({
      variables: { ids },
      onSuccess: () => {
        setClearCartOpen(false);
        refetch();
      },
    });
  };

  const handleRemoveItem = item => {
    return deleteInventoryItem({
      variables: { id: item.id },
      onSuccess: () => {
        setRemoveItem(null);
        refetch();
      },
    });
  };

  const handleQuantityChange = (item, asset, newQuantity) => {
    const hasKnownAvailability = Number.isFinite(asset?.availableQuantity);
    const maxQuantity = hasKnownAvailability ? item.quantity + asset.availableQuantity : null;
    if (maxQuantity !== null && newQuantity > maxQuantity) {
      return Promise.resolve({ quantity: `Only ${maxQuantity} total can be requested right now` });
    }

    return new Promise(resolve => {
      updateInventoryItem({
        variables: {
          inventoryItemId: item.id,
          quantity: newQuantity,
          deliveryDate: item.deliveryDate || "",
          deliveryTime: item.deliveryTime || "",
          building: item.building || "",
          floor: item.floor || "",
          room: item.room || "",
          comment: item.comment || "",
          location: item.location || "",
          street: item.street || "",
          city: item.city || "",
          zip: item.zip || "",
          poNumber: item.poNumber || "",
          idsrNumber: item.idsrNumber || "",
          eocNumber: item.eocNumber || "",
        },
        onSuccess: () => {
          setQuantityItem(null);
          refetch();
          resolve(null);
        },
        onFailure: errors => resolve(errors),
      });
    });
  };

  return (
    <>
      <Tooltip title="Shopping Cart" arrow>
        <IconButton ref={ref} onClick={handleOpen}>
          <Badge badgeContent={itemCount} color="primary" sx={styles.badge} max={99}>
            <ShoppingCartOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        sx={styles.popover}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Box px={2.5} py={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontSize={15} fontWeight={500} color="textPrimary">
            Shopping Cart {itemCount > 0 && `(${itemCount})`}
          </Typography>
          {itemCount > 0 && (
            <Button
              color="error"
              size="small"
              onClick={() => setClearCartOpen(true)}
              sx={{ textTransform: "none", fontWeight: 400, fontSize: "0.8rem" }}
            >
              Clear Cart
            </Button>
          )}
        </Box>

        <Divider />

        {itemCount === 0 ? (
          <Box py={5} px={3} textAlign="center">
            <ShoppingCartOutlinedIcon sx={{ fontSize: 40, color: "grey.300", mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              Your cart is empty
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={styles.scrollArea}>
              {inventoryItems.map((item, index) => {
                const asset = assets[item.assetId];
                const photo = asset ? getImage(asset) : null;
                const itemId = asset?.itemId || asset?.assetId || "";
                const description = asset?.description || "";

                return (
                  <React.Fragment key={item.id}>
                    {index > 0 && <Divider variant="middle" />}
                    <Box
                      sx={{
                        ...styles.itemRow,
                        position: "relative",
                        "& .remove-btn": { opacity: 0 },
                        "&:hover .remove-btn": { opacity: 1 },
                      }}
                      onClick={() => setDetailsItem({ assetId: item.assetId, projectId: item.project?.id })}
                    >
                      {photo ? (
                        <Box
                          component="img"
                          src={photo}
                          alt={description || itemId}
                          sx={styles.thumbnail}
                        />
                      ) : (
                        <Box sx={styles.thumbnailPlaceholder}>
                          <ImageOutlinedIcon fontSize="small" />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {itemId}
                        </Typography>
                        {description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                              lineHeight: 1.5,
                              mt: 0.25,
                            }}
                          >
                            {description}
                          </Typography>
                        )}
                        <Typography
                          component="button"
                          type="button"
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                          onClick={event => {
                            event.stopPropagation();
                            setQuantityItem({ item, asset });
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            lineHeight: 1.5,
                            mt: 0.25,
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            textAlign: "left",
                            fontFamily: "inherit",
                            "&:hover": {
                              color: "primary.main",
                            },
                          }}
                        >
                          <span>Qty: {item.quantity}</span>
                          <EditOutlinedIcon sx={{ fontSize: 14 }} />
                        </Typography>
                      </Box>
                      <IconButton
                        className="remove-btn"
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          setRemoveItem(item);
                        }}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: 8,
                          transform: "translateY(-50%)",
                          transition: "opacity 0.15s ease, color 0.15s ease",
                          color: "text.secondary",
                          "&:hover": { color: "error.main" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            <Divider />

            <Box px={2.5} py={2} display="flex" flexDirection="column" gap={1}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleCheckout}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "8px",
                  py: 1.1,
                  fontSize: "0.875rem",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Checkout
              </Button>
            </Box>
          </>
        )}
      </Popover>

      {detailsItem && (
        <ProjectProvider id={detailsItem.projectId} loadingComponent={<span />}>
          <ItemIdAssetModal id={detailsItem.assetId} onClose={() => setDetailsItem(null)} />
        </ProjectProvider>
      )}

      {removeItem && (
        <ConfirmDeleteModal
          title="Remove Item"
          applyLabel="Remove"
          descr="Are you sure you want to remove this item from your cart?"
          onClose={() => setRemoveItem(null)}
          onDelete={() => handleRemoveItem(removeItem)}
        />
      )}

      {quantityItem && (
        <QuantityEditModal
          onClose={() => setQuantityItem(null)}
          value={quantityItem.item.quantity}
          availableQuantity={quantityItem.asset?.availableQuantity}
          minQuantity={1}
          onChange={newQuantity =>
            handleQuantityChange(quantityItem.item, quantityItem.asset, newQuantity)
          }
        />
      )}

      <Dialog open={clearCartOpen} onClose={() => setClearCartOpen(false)}>
        <DialogTitle sx={{ color: "text.primary", fontSize: "1.4rem", fontWeight: 500, textAlign: "center" }}>
          Clear Cart
        </DialogTitle>
        <DialogContent>
          <DialogContentText textAlign="center">
            Are you sure you want to remove all items from your cart?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearCartOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleClearCart}>
            Clear Cart
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
